import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  CustomModalService,
  FileUploadComponent,
  FileUploadConfig,
  FileUploadResult,
  ProjectType,
} from '@piqet-angular/shared';
import { ProjectsService } from 'libs/data/src/lib/services/projects/projects.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'piqet-angular-tool-verification-import-modal',
  templateUrl: './tool-verification-import-modal.component.html',
  styleUrls: ['./tool-verification-import-modal.component.scss'],
})
export class ToolVerificationImportModalComponent implements OnInit {
  @ViewChild(FileUploadComponent) fileUploadComponent!: FileUploadComponent;
  @Output() importToolVerificationEvent = new EventEmitter<unknown>();

  importForm: FormGroup;
  selectedFile: File | null = null;
  fileName = '';
  isUploading = false;
  serverErrorMessage = '';
  selectedProject: ProjectType | null = ProjectType.PACKAGING_PROJECT;
  formSubmitted = false;
  isValidatingProjectCode = false;
  lastValidatedProjectCode = '';

  // Expose ProjectType enum to template
  ProjectType = ProjectType;

  // Excel upload configuration
  excelUploadConfig: Partial<FileUploadConfig> = {
    showPreview: false,
    placeholderText: 'Select excel file with tool verification data',
    selectButtonText: 'Choose excel',
    changeButtonText: 'Change',
    removeButtonText: 'Remove',
    customErrorMessage: 'Please select a valid Excel file (.xls, .xlsx, .csv) - Max 15MB',
  };

  constructor(
    private readonly modal: CustomModalService,
    private readonly fb: FormBuilder,
    private readonly toaster: ToastrService,
    private readonly _projectService: ProjectsService
  ) {
    this.importForm = this.fb.group({
      projectCode: ['', [Validators.required, Validators.minLength(3)]],
      excelFile: [null, Validators.required],
    });
  }

  ngOnInit(): void {
    // Initialize form with custom validator
    this.initializeImportForm();
  }

  private initializeImportForm(): void {
    this.importForm = this.fb.group({
      projectCode: ['', [Validators.required, Validators.minLength(3)]],
      excelFile: [null, Validators.required],
    });
  }

  selectProjectType(projectType: ProjectType): void {
    this.selectedProject = projectType;
  }

  onChangeRadio(e: Event): void {
    const target = e.target as HTMLInputElement;
    this.selectedProject = parseInt(target.value) as ProjectType;
  }

  validateProjectCode(): void {
    const projectCodeControl = this.importForm.get('projectCode');
    const projectCode = projectCodeControl?.value?.trim();

    if (!projectCode || projectCode.length < 3 || projectCode === this.lastValidatedProjectCode) {
      return;
    }

    this.isValidatingProjectCode = true;
    this.lastValidatedProjectCode = projectCode;

    this._projectService.checkProjectCode(projectCode).subscribe({
      next: response => {
        this.isValidatingProjectCode = false;
        if (response?.data === false || response?.messageType || !response) {
          projectCodeControl?.setErrors({ invalidProjectCode: true });
        } else {
          const errors = projectCodeControl?.errors;
          if (errors) {
            delete errors['invalidProjectCode'];
            projectCodeControl?.setErrors(Object.keys(errors).length ? errors : null);
          }
        }
      },
      error: error => {
        this.isValidatingProjectCode = false;
        console.error('Error validating project code:', error);
        projectCodeControl?.setErrors({ invalidProjectCode: true });
      },
    });
  }

  updateProjectCode(e?: Event): void {
    //This method can be used to update the project code value in the form when the input changes,
    //in the project code if the space is exist and bofor and after the number then replace the space with - and update the value in the form control
    const input = e?.target as HTMLInputElement;
    let projectCode = input?.value || this.importForm.get('projectCode')?.value || '';
    projectCode = projectCode.trim().replace(/\s+/g, '-');
    this.importForm.get('projectCode')?.setValue(projectCode);
  }

  onFileSelected(result: FileUploadResult): void {
    if (result.isValid && result.file) {
      this.selectedFile = result.file;
      this.fileName = result?.fileName ?? '';
      this.importForm.patchValue({ excelFile: result.file });
      this.serverErrorMessage = '';
    } else {
      this.resetFileData();
      this.serverErrorMessage = 'Invalid file selected. Please choose a valid Excel file.';
    }
  }

  onFileRemoved(): void {
    this.resetFileData();
  }

  onRemoveConfirmation(): void {
    this.fileUploadComponent?.reset();
    this.onFileRemoved();
  }

  resetFileData(): void {
    this.selectedFile = null;
    this.fileName = '';
    this.importForm.patchValue({ excelFile: null });
  }

  onClose(): void {
    this.importForm.reset();
    this.resetFileData();
    this.serverErrorMessage = '';
    this.selectedProject = null;
    this.formSubmitted = false;
    this.isValidatingProjectCode = false;
    this.lastValidatedProjectCode = '';
    this.modal.close();
  }

  onSave(): void {
    this.formSubmitted = true;

    // Validate project code before saving if not already validated
    const projectCode = this.importForm.get('projectCode')?.value?.trim();
    if (projectCode && projectCode !== this.lastValidatedProjectCode) {
      this.validateProjectCode();
      return; // Wait for validation to complete
    }

    if (
      this.importForm.valid &&
      this.selectedFile &&
      this.selectedProject &&
      !this.isValidatingProjectCode
    ) {
      this.isUploading = true;

      const formData = new FormData();
      formData.append('excelFile', this.selectedFile);
      formData.append('projectCode', this.importForm.get('projectCode')?.value);
      formData.append('projectType', this.selectedProject.toString());
      this.importToolVerificationEvent.emit(formData);
      this.isUploading = false;
      this.toaster.success('Tool verification data imported successfully!');
      this.modal.close();
    } else {
      Object.keys(this.importForm.controls).forEach(key => {
        this.importForm.get(key)?.markAsTouched();
      });
    }
  }
}
